import { useQuery } from "react-query";
import { z } from "zod";

import { zodParseWithLog } from "src/api/apiErrorLogs";
import { loggedInApiCall } from "src/api/axiosClient/userApi";

export const CpcSchema = z.object({
    code: z.string(),
    "@id": z.string().optional(),
    title: z.string(),
    level: z.number().optional(),
    version: z.string().optional(),
});

export const CpcArraySchema = z.array(CpcSchema);

export const cpcSelectorApiKey = "cpcSelector";

export type Cpc = z.infer<typeof CpcSchema>;

type UseCpcSelectorOptions = {
    searchText?: string;
    enabled?: boolean;
    itemsPerPage?: number;
};

const isLikelyCpcCode = (value: string): boolean => /^[\d.]+$/.test(value.trim());

export const useCpcSelector = (options: UseCpcSelectorOptions = {}) => {
    const { searchText = "", enabled = true, itemsPerPage = 50 } = options;
    const trimmedSearch = searchText.trim();

    const query = useQuery({
        queryKey: [cpcSelectorApiKey, trimmedSearch, itemsPerPage],
        queryFn: async () => {
            const params: Record<string, string | number | Record<string, string>> = {
                page: 1,
                itemsPerPage,
                order: { code: "ASC" },
            };

            if (trimmedSearch !== "") {
                if (isLikelyCpcCode(trimmedSearch)) {
                    params.code = trimmedSearch;
                } else {
                    // CPC labels in Sure are stored in English (UN CPC v3 pivot).
                    params.title = trimmedSearch;
                }
            }

            const response = await loggedInApiCall.get<{ "hydra:member": unknown; "hydra:totalItems": number }>(
                "/api/cpc",
                { params },
            );

            return response.data;
        },
        select: (data) => {
            if (!data?.["hydra:member"]) {
                return { cpcItems: [], totalItems: 0 };
            }

            const cpcItems = zodParseWithLog(data["hydra:member"], CpcArraySchema, cpcSelectorApiKey);

            return {
                cpcItems,
                totalItems: data["hydra:totalItems"] ?? cpcItems.length,
            };
        },
        enabled,
        staleTime: 30_000,
        cacheTime: 300_000,
    });

    return {
        cpcItems: query.data?.cpcItems ?? [],
        totalItems: query.data?.totalItems ?? 0,
        ...query,
    };
};
